import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { Representative } from '../../model/representative';
import { FormBuilder, FormGroup } from '@angular/forms';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { Subscription, of, switchMap } from 'rxjs';
import { RepresentativeService } from '../../service/data/representative.service';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { ENGLISH_CULTURE, LanguageService, SWEDISH_CULTURE } from '../../../core/service/language.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { TenantCategory, TenantCategoryToString, TenantState } from '../../model/const/enums';
import { MessageService } from '../../../core/service/message.service';
import { ContactService } from '../../service/data/contact.service';
import { ContactIdKind } from '../../model/contact';
import { IdType } from '../../model/id-type';
import { SelectionItem } from '../../../core/component/select/select.component';
import { FormControlEx } from '../../../core/component/forms/form-control';

@Component({
    selector: 'app-representatives-form',
    templateUrl: './representatives-form.component.html',
    styleUrls: ['./representatives-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RepresentativesFormComponent implements OnInit, OnDestroy {
  constructor(
    private _fb: FormBuilder,
    private _fcf: FormControlExFactory,
    private _dataService: RepresentativeService,
    private _actionService: ActionWindowService,
    private _clipboard: Clipboard,
    private _messageService: MessageService,
    private _contactService: ContactService
  ) {}

  form: FormGroup;
  private _sub: Subscription = new Subscription();
  private get categoryControl(): FormControlEx {
    return this.form?.controls['contactCategory'] as FormControlEx;
  }
  @Input() dataContext: Representative;
  @Input() innerForm: FormGroup;
  @Input() isBrf: boolean = false;
  @Output() cancelOperation = new EventEmitter<void>();
  @Output() formSubmitted = new EventEmitter<void>();
  @Output() formStatusChanged = new EventEmitter<boolean>();

  existingContactName: string;
  idTypeSelection: SelectionItem[];
  private _idTypes: IdType[];
  categories = [
    { name: TenantCategoryToString(TenantCategory.PhysicalPerson), value: TenantCategory.PhysicalPerson },
    { name: TenantCategoryToString(TenantCategory.Company), value: TenantCategory.Company },
  ];
  cultures = [
    {
      value: SWEDISH_CULTURE,
      name: 'SWEDISH_TEXT',
    },
    {
      value: ENGLISH_CULTURE,
      name: 'ENGLISH_TEXT',
    },
  ];

  get contactIdentificationType(): ContactIdKind {
    return this.form?.controls['contactIdentificationType']?.value || ContactIdKind.None;
  }

  get submitButtonDisabled(): boolean {
    return this.form?.pristine ?? true;
  }

  setLanguageRadio() {
    return !this.dataContext.id || this.dataContext.contactState === TenantState.Registered;
  }

  setInvitationButtons() {
    return this.dataContext.id && this.dataContext.contactState === TenantState.Registered;
  }

  ngOnInit(): void {
    this.form = this.createForm();
    this._getIdTypes(this.dataContext.contactIdentificationType, this.dataContext.contactCategory);
    this._setupCategoryIdTypesChanges();
    this.setInvitationButtons();
    this.setLanguageRadio();

    this.form.statusChanges.subscribe(() => {
      this.formStatusChanged.emit(this.form.pristine);
    });
  }

  ngOnDestroy(): void {
    this._sub.unsubscribe();
  }

  private createForm(): FormGroup {
    var group = this._fb.group({
      id: this._fcf.createHidden(this.dataContext.id),
      contactId: this._fcf.createHidden(this.dataContext.contactId),
      contactState: this._fcf.createHidden(this.dataContext.contactState),
      leaseAgreementId: this._fcf.createHidden(this.dataContext.leaseAgreementId),
      contactRegistrationKey: this._fcf.createHidden(this.dataContext.contactRegistrationKey),
      contactName: this._fcf.createMandatory('TENANT.NAME', this.dataContext.contactName),
      contactEmail: this._fcf.createEmailMandatory('TENANT.EMAIL', this.dataContext.contactEmail),
      contactPhoneNumber: this._fcf.create('TENANT.PHONE', this.dataContext.contactPhoneNumber),
      faultNotificationsAccess: this._fcf.create(
        'REPRESENTATIVE.FORM.ACCESS_FAULT_NOTIFICATION',
        this.dataContext.faultNotificationsAccess
      ),
      invoicesAccess: this._fcf.create('REPRESENTATIVE.FORM.ACCESS_INVOICE', this.dataContext.invoicesAccess),
      dwellingInspectionsAccess: this._fcf.create(
        'REPRESENTATIVE.FORM.ACCESS_DWELLING_INSPECTION',
        this.dataContext.dwellingInspectionsAccess
      ),
      leaseAgreementsAccess: this._fcf.create('REPRESENTATIVE.FORM.ACCESS_LEASE_AGREEMENTS', this.dataContext.leaseAgreementsAccess),
      documentsAccess: this._fcf.create('REPRESENTATIVE.FORM.ACCESS_DOCUMENTS', this.dataContext.documentsAccess),
      contactCategory: this._fcf.create('TENANT.CATEGORY', this.dataContext.contactCategory),
      contactIdentificationType: this._fcf.create('TENANT.ID_TYPE.ID_TYPE', this.dataContext.contactIdentificationType),
      contactIdentification: this._fcf.create('TENANT.ID_TYPE.ID_NUMBER', this.dataContext.contactIdentification),
      inviteCultureName: this._fcf.create('LANGUAGE_TEXT', this.dataContext.inviteCultureName ?? this.cultures[0].value),
    });

    this._setupFormDuplicateContactIdentification(group);
    this._sub.add(group.controls['inviteCultureName'].valueChanges.subscribe(x => (this.dataContext.inviteCultureName = x)));

    // If we create a representative from an existing contact, unlock the submit button.
    if (!this.dataContext.id && this.dataContext.contactId) {
      group.markAsTouched();
      group.markAsDirty();
    }

    return group;
  }

  private _getIdTypes(contactIdType: ContactIdKind, category: TenantCategory): void {
    // First set the current value (and lock the control):
    if (contactIdType && category) {
      const idType = new IdType(category, contactIdType);
      this.idTypeSelection = [new SelectionItem(idType.caption, idType.idType)];
    }

    (this.form.controls['contactIdentificationType'] as FormControlEx).isReadonly = true;
    // Then load all the available options (and unlock the control):
    this._sub.add(
      this._contactService.getIdTypes().subscribe(types => {
        this._idTypes = types;
        this.idTypeSelection = this._idTypes.filter(i => i.category === category).map(i => new SelectionItem(i.caption, i.idType));
        if (this.dataContext && this.dataContext.contactIdentificationType) {
        }
        if (this.dataContext && this.dataContext.isNew && !this.dataContext.contactIdentificationType) {
          this.form.controls['contactIdentificationType'].setValue(this.idTypeSelection[0].value);
        }
        (this.form.controls['contactIdentificationType'] as FormControlEx).isReadonly = false;
      })
    );
  }

  private _setupCategoryIdTypesChanges(): void {
    this._sub.add(
      this.categoryControl.valueChanges.subscribe((value: TenantCategory) => {
        if (this._idTypes) {
          this.idTypeSelection = this._idTypes.filter(i => i.category === value).map(i => new SelectionItem(i.caption, i.idType));
          if (!this.idTypeSelection.find(s => s.value == this.form.controls['contactIdentificationType'].value)) {
            this.form.controls['contactIdentificationType'].setValue(this.idTypeSelection[0].value);
          }
        }
      })
    );
    this.categoryControl.updateValueAndValidity();
  }

  private _setupFormDuplicateContactIdentification(form: FormGroup): void {
    this._sub.add(
      form.controls['contactIdentification'].valueChanges
        .pipe(
          switchMap((val: string) => {
            this.existingContactName = null;
            if (val && val.length > 10) {
              return this._contactService.getNameByCivicOrOrgNo(this.dataContext.contactId, val);
            }
            return of(null);
          })
        )
        .subscribe(name => (this.existingContactName = name))
    );
  }

  onSubmit() {
    if (this.form.pristine) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const representative = <Representative>Object.assign(new Representative(), this.form.value);
    const completeAction = (value: any) => {
      this._actionService.complete(value);
      this.formSubmitted.emit();
    };

    if (this.dataContext.id) {
      this._sub.add(
        this._dataService.change(representative).subscribe(res => {
          completeAction(res);
        })
      );
    } else {
      this._sub.add(
        this._dataService.add(representative).subscribe(createdRepresentative => {
          completeAction(createdRepresentative.id);
          if (representative.contactId) {
            if (representative.contactState === TenantState.Registered) {
              this._sendRegistrationKey(representative);
            }
          } else {
            this._dataService.get(createdRepresentative.id).subscribe(res => {
              representative.contactId = res.contactId;
              this._sendRegistrationKey(representative);
            });
          }
        })
      );
    }
  }

  private _sendRegistrationKey(representative: Representative) {
    this._dataService
      .sendRegistrationKey(representative.contactId, representative.contactEmail, representative.inviteCultureName)
      .subscribe();
  }

  onFormCancel() {
    this.cancelOperation.emit();
  }

  onSendEmail(): void {
    this._sub.add(
      this._dataService
        .sendRegistrationKey(
          this.dataContext.contactId,
          this.form.controls['contactEmail'].value,
          this.form.controls['inviteCultureName'].value
        )
        .subscribe()
    );
  }

  onDelete(): void {
    this._sub.add(
      this._dataService.deleteWithConfirmation(this.dataContext.id).subscribe(x => {
        this.formSubmitted.emit();
      })
    );
  }

  onCopyToClipboard() {
    this._clipboard.copy(this.dataContext.contactRegistrationKey);
    this._messageService.showMessage('REPRESENTATIVE.FORM.COPY_TO_CLIPBOARD_MESSAGE');
  }
}
